import cytoscape from 'cytoscape';
import dagre from 'cytoscape-dagre';
import fcose from 'cytoscape-fcose';
import spread from 'cytoscape-spread';
import cola from 'cytoscape-cola';
import cise from 'cytoscape-cise';
import coseBilkent from 'cytoscape-cose-bilkent';
import * as Comlink from 'comlink';

const layoutProperties = {
    forceDirected: {
        name: 'fcose',
        extension: fcose
    },
    forceDirectedOptimised: {
        name: 'cose-bilkent',
        extension: coseBilkent
    },
    hierarchy: {
        name: 'dagre',
        extension: dagre
    },
    spread: {
        name: 'spread',
        extension: spread
    },
    cola: {
        name: 'cola',
        extension: cola
    },
    concentric: {
        name: 'concentric',
        extension: spread
    },
    circular: {
        name: 'cise',
        extension: cise
    }
};

const getLayoutDefaultsForType = (layoutType) => {
    switch (layoutType) {
        case 'dagre':
            return {
                animate: false,
                nodeSep: 50,
                edgeSep: 50
            };
        case 'spread':
            return {
                minDist: 50,
                expandingFactor: 1,
                prelayout: {
                    name: 'cose-bilkent',
                    numIter: 30000,
                    nodeRepulsion: 15000,
                    idealEdgeLength: 160,
                    animate: false,
                    nestingFactor: 0,
                    edgeElasticity: 0.05,
                    tilingPaddingVertical: 50,
                    tilingPaddingHorizontal: 50
                }
            };
        case 'forceDirectedOptimised':
            return {
                numIter: 10000,
                nodeRepulsion: 15000,
                idealEdgeLength: 160,
                nestingFactor: 0,
                edgeElasticity: 0.05,
                tilingPaddingVertical: 50,
                tilingPaddingHorizontal: 50
            };
        case 'forceDirected':
            return {
                numIter: 8000,
                idealEdgeLength: 80,
                nodeRepulsion: 20000,
                gravityRange: 10
            };
        case 'concentric':
            return {
                minNodeSpacing: 50,
                spacingFactor: 1,
                concentric(node) {
                    return node.degree();
                }
            };
        default:
            return {};
    }
};

function nodeWidth(el) {
    const elData = el.data();
    const degree = el.degree();

    const isKPI = Boolean(elData.workspaceID);
    if (isKPI) {
        return 400;
    }

    const isSpace = elData['type'] ? elData['type'][0] === 'space' : false;
    const isApp = elData['type'] ? elData['type'][0] === 'app' : false;
    return isSpace && degree > 0 ? 160 : isApp && degree > 0 ? 120 : 80;
}

const NODE_HEIGHTS = {
    appHeight: 120,
    minSpaceHeight: 155,
    objectHeight: 80,
    monitorHeight: 59 * 2.2,
    monitorExcess: 50,
    pluginsBuffer: 140
};

function nodeHeight(el) {
    const elData = el.data();

    const isKPI = Boolean(elData.workspaceID);
    const isSpace = elData['type'] ? elData['type'][0] === 'space' : false;
    const isApp = elData['type'] ? elData['type'][0] === 'app' : false;

    const {
        appHeight,
        minSpaceHeight,
        objectHeight,
        monitorHeight,
        monitorExcess,
        pluginsBuffer
    } = NODE_HEIGHTS;

    if (isApp) {
        return appHeight;
    } else if (isKPI) {
        const kpis = elData['kpis'];
        const kpisCount = kpis?.length || 0;
        const tiles = elData['tiles'];
        const tilesCount = tiles?.length || 0;


        if (kpisCount === 0 && tilesCount === 0) {
            return minSpaceHeight;
        }

        // Calculations based on the rendered browser heights for these components.
        // This is a bit of a magic formula as the dagre layout calculates an average node
        // height and applies that as the spacing in all ranks height.
        // Additionally adds a buffer on the end that assumes two rows of plugins
        return Math.max(
            (Math.min(11, kpisCount + tilesCount) * monitorHeight - monitorExcess) + pluginsBuffer,
            minSpaceHeight
        );
    } else if (isSpace) {
        return minSpaceHeight;
    }

    return objectHeight;
}

function generateNetwork(data, networkConfig) {
    const layout = networkConfig.layoutType || 'spread';
    const { name: layoutName, extension: layoutExtension } = layoutProperties[layout];

    // Set cytoscape extension to correct setting for layout, defaults to fcose
    cytoscape.use(dagre);
    cytoscape.use(coseBilkent);
    cytoscape.use(layoutExtension);

    return new Promise((resolve) => {
        const { nodes, edges } = data;

        if (nodes && edges) {
            const options = {
                headless: true,
                styleEnabled: true,
                layout: {
                    name: layoutName,
                    fit: true,
                    padding: 0,
                    animate: false,
                    quality: 'proof',
                    nodeDimensionsIncludeLabels: true,
                    ...getLayoutDefaultsForType(layoutName),
                    ...networkConfig
                },
                style: [
                    {
                        selector: 'node',
                        style: {
                            width: nodeWidth,
                            height: nodeHeight
                        }
                    }
                ],
                elements: [...nodes, ...edges]
            };

            const cy = cytoscape(options);

            cy.ready(() => {
                const positionedNodes = cy.nodes().map((ele) => {
                    const nodeData = ele.data();
                    return {
                        id: nodeData.id,
                        data: nodeData,
                        hidden: Boolean(nodeData.hidden),
                        position: ele.position()
                    };
                });

                const edgesWithId = cy.edges().map((ele) => {
                    const edgeData = ele.data();
                    const { id, source, target, type, label } = edgeData;

                    return {
                        id,
                        source,
                        target,
                        type,
                        data: edgeData,
                        ...(label && { label })
                    };
                });

                resolve({ nodes: positionedNodes, edges: edgesWithId });
            });
        }
    });
}

Comlink.expose({ generateNetwork });
